// Der Ordner „KMVS" in der Kundenakte (PO 28.09.2026).
//
// „Dann in Alte Kundenakten nicht Vorgänge und darunter die Dokumente, sondern
// einen Ordner KMVS anzeigen, unter dem gibt es die Unterordner Kontakt,
// Risiken, Verträge, Schäden, Vorgänge, Zahlungsverkehr — und bei Vorgänge je
// Vorgang ein Ordner mit dem Betreff als Namen."
//
// Die Ordner gibt es nirgends als Daten: Sie entstehen aus den Verknüpfungen
// der Dokumente (Vorgang, Schaden, Vertrag, Risikoobjekt). Deshalb ist der
// ganze Teilbaum VIRTUELL — nur anzeigen, nicht verschieben, keine neuen
// Ordner darin (PO-Entscheidung 28.09.2026).
//
// Rein und ohne React, damit die Zuordnung direkt testbar ist
// (kmvsOrdner.test.ts).

import type { ExplorerFolder } from "./explorerModel";

/**
 * Wurzelpfad des virtuellen Teilbaums. Das führende Wortverbindungszeichen
 * (U+2060) ist unsichtbar und nicht tippbar: Ein Ordner „KMVS", den jemand in
 * der alten Akte selbst angelegt hat, bekommt damit nie denselben Pfad.
 */
export const KMVS_WURZEL = "⁠KMVS/";

export type KmvsBereich = "kontakt" | "risiken" | "vertraege" | "schaeden" | "vorgaenge" | "zahlungsverkehr";

/** Reihenfolge und Beschriftung — so, wie der PO sie aufgezählt hat. */
export const KMVS_BEREICHE: ReadonlyArray<{ key: KmvsBereich; name: string }> = [
  { key: "kontakt", name: "Kontakt" },
  { key: "risiken", name: "Risiken" },
  { key: "vertraege", name: "Verträge" },
  { key: "schaeden", name: "Schäden" },
  { key: "vorgaenge", name: "Vorgänge" },
  { key: "zahlungsverkehr", name: "Zahlungsverkehr" },
];

export const istKmvsPfad = (pfad: string): boolean => pfad.startsWith(KMVS_WURZEL);

/** Was die Zuordnung von einem Dokument braucht. */
export type KmvsDokument = {
  contractId?: number | null;
  damageId?: number | null;
  riskObjectId?: number | null;
  vorgang?: { id: number; vorgangNbr: string | null; titel: string | null } | null;
};

/**
 * In welchen Bereich gehört ein Dokument? Ein Dokument steht in genau einem
 * Ordner — der spezifischste Bezug gewinnt: Vorgang vor Schaden vor Vertrag
 * vor Risiko. Ein Schadendokument, das in einem Vorgang bearbeitet wurde, sucht
 * man im Vorgang; ein Vertragsdokument ohne Vorgang beim Vertrag.
 *
 * Für den Zahlungsverkehr tragen Dokumente heute keinen Bezug; der Ordner
 * bleibt leer, bis es einen gibt.
 */
export const bereichVon = (d: KmvsDokument): KmvsBereich => {
  if (d.vorgang?.id) return "vorgaenge";
  if (d.damageId) return "schaeden";
  if (d.contractId) return "vertraege";
  if (d.riskObjectId) return "risiken";
  return "kontakt";
};

/** Ordnername eines Vorgangs: der Betreff, ersatzweise die Nummer. */
export const vorgangsOrdnerName = (v: { id: number; vorgangNbr: string | null; titel: string | null }): string =>
  v.titel?.trim() || (v.vorgangNbr ? `Vorgang ${v.vorgangNbr}` : `Vorgang ${v.id}`);

export type KmvsStruktur<D> = {
  /** Unterordner einer Ebene im KMVS-Teilbaum ("" = die Wurzel der Akte → der KMVS-Ordner selbst). */
  ordner: (pfad: string) => ExplorerFolder[];
  /** Dateien direkt in einem KMVS-Ordner. */
  dateien: (pfad: string) => D[];
  /** Anzeigename eines Pfadsegments (für die Brotkrumen). */
  name: (pfadBisSegment: string) => string | undefined;
  /** Alle Dokumente im KMVS-Teilbaum. */
  anzahl: number;
};

/**
 * Baut den KMVS-Teilbaum aus den geladenen Dokumenten.
 *
 * Die festen Bereiche stehen immer da, auch leer: Wer „Schäden" sucht, soll
 * den Ordner finden und sehen, dass nichts darin liegt — ein fehlender Ordner
 * sähe aus wie ein Fehler.
 */
export const baueKmvsStruktur = <D extends KmvsDokument>(docs: readonly D[]): KmvsStruktur<D> => {
  const jeBereich = new Map<KmvsBereich, D[]>(KMVS_BEREICHE.map((b) => [b.key, []]));
  const jeVorgang = new Map<number, { name: string; docs: D[] }>();
  for (const d of docs) {
    const b = bereichVon(d);
    if (b === "vorgaenge" && d.vorgang) {
      const eintrag = jeVorgang.get(d.vorgang.id) ?? { name: vorgangsOrdnerName(d.vorgang), docs: [] };
      eintrag.docs.push(d);
      jeVorgang.set(d.vorgang.id, eintrag);
    } else {
      jeBereich.get(b)!.push(d);
    }
  }

  const bereichsPfad = (key: KmvsBereich) => `${KMVS_WURZEL}${key}/`;
  const vorgangsPfad = (id: number) => `${KMVS_WURZEL}vorgaenge/${id}/`;

  const ordner = (pfad: string): ExplorerFolder[] => {
    if (pfad === "") {
      return [{ kind: "folder", name: "KMVS", path: KMVS_WURZEL, count: docs.length, reihenfolge: -1, virtuell: true }];
    }
    if (pfad === KMVS_WURZEL) {
      return KMVS_BEREICHE.map((b, i) => ({
        kind: "folder" as const,
        name: b.name,
        path: bereichsPfad(b.key),
        count:
          b.key === "vorgaenge"
            ? [...jeVorgang.values()].reduce((n, v) => n + v.docs.length, 0)
            : jeBereich.get(b.key)!.length,
        reihenfolge: i,
        virtuell: true,
      }));
    }
    if (pfad === bereichsPfad("vorgaenge")) {
      return [...jeVorgang.entries()].map(([id, v]) => ({
        kind: "folder" as const,
        name: v.name,
        path: vorgangsPfad(id),
        count: v.docs.length,
        virtuell: true,
      }));
    }
    return [];
  };

  const dateien = (pfad: string): D[] => {
    for (const b of KMVS_BEREICHE) {
      if (b.key !== "vorgaenge" && pfad === bereichsPfad(b.key)) return jeBereich.get(b.key)!;
    }
    const m = pfad.startsWith(`${KMVS_WURZEL}vorgaenge/`)
      ? /^(\d+)\/$/.exec(pfad.slice(`${KMVS_WURZEL}vorgaenge/`.length))
      : null;
    return m ? jeVorgang.get(Number(m[1]))?.docs ?? [] : [];
  };

  const name = (bis: string): string | undefined => {
    if (bis === KMVS_WURZEL) return "KMVS";
    const b = KMVS_BEREICHE.find((x) => bis === bereichsPfad(x.key));
    if (b) return b.name;
    const m = bis.startsWith(`${KMVS_WURZEL}vorgaenge/`)
      ? /^(\d+)\/$/.exec(bis.slice(`${KMVS_WURZEL}vorgaenge/`.length))
      : null;
    return m ? jeVorgang.get(Number(m[1]))?.name ?? `Vorgang ${m[1]}` : undefined;
  };

  return { ordner, dateien, name, anzahl: docs.length };
};
